import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../../../../../redux/api";

const buildOption = (p) => ({
  value: p?.id,
  label: p?.name ?? `Product #${p?.id}`,
  data: {
    name: p?.name ?? "",
    ar_name: p?.ar_name ?? "",
    image: p?.image ?? "",
    barcode: p?.barcode ?? "",
    size: p?.size ?? null,
    color: p?.color ?? null,
  },
});

// Drop a trailing size/volume token so a variant's base name is searchable:
// "Brit Sheer - EDT (Women) 100 ml" -> "Brit Sheer - EDT (Women)", which the
// 50 ml sibling's name also contains. Names without a size token are left as-is.
const stripSizeSuffix = (name) =>
  String(name || "")
    .replace(
      /[\s\-–—,]*\d+(\.\d+)?\s*(ml|l|g|gr|gm|mg|kg|oz|fl\.?\s*oz)\b\.?\s*$/i,
      "",
    )
    .trim();

/**
 * Async search for the product-link picker. Hits the same `admin/products`
 * endpoint that already powers the list page (which matches `search_key`
 * against name + barcode server-side), but bypasses Redux so typing here
 * does not churn the global `productList` other screens consume.
 *
 * - Debounces input (default 300ms) so a 13-digit barcode fires one request
 * - Cancels in-flight requests via AbortController on each keystroke
 * - Caches results per query within the form session
 * - Pre-suggests likely siblings: before the user types, the menu is seeded
 *   with products matching the current product's base name (size stripped),
 *   instead of dumping page 1 of every product.
 *
 * @param {object} args
 * @param {number} [args.excludeProductId] - the product currently being edited
 * @param {string} [args.suggestByName]    - current product name; drives the
 *                                            pre-typed sibling suggestions
 * @param {number} [args.debounceMs=300]
 */
export const useProductSearch = ({
  excludeProductId,
  suggestByName = "",
  debounceMs = 300,
} = {}) => {
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const cacheRef = useRef(new Map());

  const defaultQuery = stripSizeSuffix(suggestByName);

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const fetchProducts = useCallback(
    async (searchKey, signal) => {
      const res = await axiosInstance.get("admin/products", {
        params: { page: 1, search_key: searchKey, status: "" },
        signal,
      });
      const list = res?.data?.response?.products ?? [];
      return list
        .filter((p) => (excludeProductId ? p?.id !== excludeProductId : true))
        .map(buildOption);
    },
    [excludeProductId],
  );

  // Pre-fetched sibling suggestions shown before the user types. Re-runs when
  // the base name becomes available (product loads async on edit). One request,
  // replacing the previous "page 1 of everything" default load.
  const [defaultOptions, setDefaultOptions] = useState([]);
  useEffect(() => {
    if (!defaultQuery) {
      setDefaultOptions([]);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    fetchProducts(defaultQuery, controller.signal)
      .then((opts) => {
        if (cancelled) return;
        cacheRef.current.set(defaultQuery.toLowerCase(), opts);
        setDefaultOptions(opts);
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        if (!cancelled) setDefaultOptions([]);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [defaultQuery, fetchProducts]);

  const loadOptions = useCallback(
    (input) => {
      const key = String(input ?? "").trim().toLowerCase();
      if (!key) return Promise.resolve(defaultOptions);

      if (cacheRef.current.has(key)) {
        return Promise.resolve(cacheRef.current.get(key));
      }

      return new Promise((resolve) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
          if (abortRef.current) abortRef.current.abort();
          const controller = new AbortController();
          abortRef.current = controller;

          try {
            const opts = await fetchProducts(key, controller.signal);
            cacheRef.current.set(key, opts);
            resolve(opts);
          } catch (err) {
            if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
              // Newer keystroke aborted us — let the latest request resolve.
              return;
            }
            resolve([]);
          }
        }, debounceMs);
      });
    },
    [defaultOptions, fetchProducts, debounceMs],
  );

  return { loadOptions, defaultOptions };
};
